import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { HeaderSettings } from '../../components/Headerfiles/types';
import type { Question, QuestionDifficulty, SectionConfig, SectionQuestionType, SavedExam, ResearchResult, SavedResearch } from '../../../services/types';
import { DIFFICULTY_OPTIONS, EXAM_TYPE_OPTIONS } from '../../components/constants';
import { exportPDF, exportWord } from '../../components/gemini-files/utils/exportUtils';
import { exportResearchPDF, exportResearchWord, exportBibliography } from '../../components/gemini-files/utils/researchExportUtils';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/gemini-files/Card';
import Button from '../../components/gemini-files/Button';
import Select from '../../components/gemini-files/Select';
import Input from '../../components/gemini-files/Input';
import Textarea from '../../components/gemini-files/Textarea';
import Checkbox from '../../components/gemini-files/Checkbox';
import Progress from '../../components/gemini-files/Progress';
import { auth } from "../../firebase/config";
import { extractTextFromPDF, validatePDFFile, formatContentForPrompt, type PDFExtractResult } from '../../utils/pdfExtractor';
// Helper function to convert HTML to plain text for editing
const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  // Replace <br/> and <br> with newlines
  let text = html.replace(/<br\s*\/?>/gi, '\n');
  // Remove all other HTML tags
  text = text.replace(/<[^>]*>/g, '');
  // Decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

export const EditableQuestionCard: React.FC<{
  question: Question;
  onSave: (updatedQuestion: Question) => void;
  onCancel: () => void;
}> = ({ question, onSave, onCancel }) => {
  // Convert HTML to plain text for editing
  const [editedQuestion, setEditedQuestion] = useState<EditableQuestion>({
    ...question,
    question: htmlToPlainText(question.question),
    answer: htmlToPlainText(question.answer),
    explanation: htmlToPlainText(question.explanation),
  });

  const handleChange = (field: 'question' | 'answer' | 'explanation', value: string) => {
    setEditedQuestion((prev) => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index: number, value: string) => {
    setEditedQuestion((prev) => {
      const newOptions = [...(prev.options || [])];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  };

  return (
    <Card 
      key={editedQuestion.id} 
      className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm ring-2 ring-primary/50"
    >
      <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
        <div className="text-sm font-semibold text-[#2563EB] dark:text-[#3B82F6]">Editing Question...</div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div>
          <label className="font-bold text-sm mb-1 block">Question:</label>
          <Textarea 
            value={editedQuestion.question}
            onChange={(e) => handleChange('question', e.target.value)}
            className="min-h-[120px] font-mono text-xs bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
            rows={8}
          />
        </div>
        {editedQuestion.options && editedQuestion.type === 'MCQ' && (
          <div>
            <label className="font-bold text-sm mb-1 block">Options:</label>
            <div className="space-y-2">
              {editedQuestion.options.map((option: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="font-mono font-bold">{String.fromCharCode(65 + index)}.</span>
                  <Input 
                    value={option}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOptionChange(index, e.target.value)}
                    className="font-mono text-xs"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="font-bold text-sm mb-1 block">Answer / Marking Scheme:</label>
          <Textarea 
            value={editedQuestion.answer}
            onChange={(e) => handleChange('answer', e.target.value)}
            className="min-h-[100px] font-mono text-xs bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
            rows={6}
          />
        </div>
        <div>
          <label className="font-bold text-sm mb-1 block">Explanation:</label>
          <Textarea 
            value={editedQuestion.explanation}
            onChange={(e) => handleChange('explanation', e.target.value)}
            className="min-h-[100px] font-mono text-xs bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
            rows={6}
          />
        </div>
      </CardContent>
      <div className="p-4 sm:p-6 pt-0 flex justify-end items-center space-x-2 border-t border-[#E2E8F0] dark:border-[#1E293B]">
        <Button 
          variant="outline" 
          onClick={onCancel}
          className="m-3 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
        >
          Cancel
        </Button>
        <Button 
          onClick={() => onSave(editedQuestion)}
          className="m-3 h-10 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg"
        >
          Save Changes
        </Button>
      </div>
    </Card>
  );
};

export const SaveExamModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (examName: string) => void;
  initialName: string;
  errorMessage?: string;
}> = ({ isOpen, onClose, onSave, initialName, errorMessage }) => {
  const [examName, setExamName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSave(examName);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" 
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-exam-title"
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Card className="w-full max-w-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-lg">
          <CardHeader className="p-4 border-b border-[#E2E8F0] dark:border-[#1E293B]">
            <div id="save-exam-title">
              <CardTitle className="text-lg text-black dark:text-white">Save Exam</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Exam Name
              </label>
              <input
                ref={inputRef}
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter exam name"
                className="h-10 w-full px-3 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                aria-label="Exam name"
              />
              {errorMessage && (
                <p className="text-sm text-destructive mt-2">{errorMessage}</p>
              )}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="m-3 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!examName.trim()}
                className="m-3 h-10 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg disabled:opacity-50"
              >
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export interface SectionState extends SectionConfig {
  isMCQ: boolean;
  numOptions: number;
  questionStyle: 'single' | 'sub-questions' | 'both';
}

interface EditableQuestion extends Question {
  options?: string[];
}

export interface GeneratorViewProps {
  addQuestionsToBank: (questions: Question[]) => void;
  addSavedExam: (exam: SavedExam) => void;
  initialExam: SavedExam | null;
  savedExams: SavedExam[];
  onCancelEdit: () => void;
  schoolId?: string | null;
  headerSettings?: HeaderSettings;
  onQuestionsGenerated?: (questions: Question[], params: any) => void;
}

export const GeneratorView: React.FC<GeneratorViewProps> = ({ addQuestionsToBank, addSavedExam, initialExam, savedExams, onCancelEdit, schoolId, headerSettings, onQuestionsGenerated }) => {
  const [numSections, setNumSections] = useState(1);
  const [params, setParams] = useState({
    examTitle: 'Midterm Exam',
    examType: EXAM_TYPE_OPTIONS[0],
    subject: 'Mathematics',
    topics: [] as string[],
    difficulty: DIFFICULTY_OPTIONS[1] as QuestionDifficulty,
    customInstructions: '',
    totalMarks: 100,
    sections: [] as SectionState[],
  });
  
  const [customExamType, setCustomExamType] = useState('');
  const [currentTopicInput, setCurrentTopicInput] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [includeAnswers, setIncludeAnswers] = useState(true);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [currentExamId, setCurrentExamId] = useState<string | null>(null);
  const isEditing = !!initialExam;
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  
  // PDF Upload State
  const [pdfMode, setPdfMode] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfContent, setPdfContent] = useState<PDFExtractResult | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfProgressStatus, setPdfProgressStatus] = useState('');
  const pdfInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (initialExam) {
      setParams(initialExam.params as any);
      setGeneratedQuestions(initialExam.questions);
      setCurrentExamId(initialExam.id);
      setCustomExamType(initialExam.params.examType === 'Custom' ? initialExam.params.examType : '');
    }
  }, [initialExam]);

  useEffect(() => {
    if (generatedQuestions.length > 0 && onQuestionsGenerated) {
      onQuestionsGenerated(generatedQuestions, params);
    }
  }, [generatedQuestions, onQuestionsGenerated, params]);

  useEffect(() => {
    let interval: number;
    if (isLoading) {
      setProgress(10);
      interval = window.setInterval(() => {
        setProgress(prev => {
          if (prev < 90) {
            const increment = Math.random() * 10;
            return Math.min(prev + increment, 90);
          } else if (prev < 99) {
            return prev + 0.5;
          } else {
            clearInterval(interval);
            return 99;
          }
        });
      }, 800);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoading]);

  // When new generated questions arrive, trigger MathJax typesetting so equations render nicely
  useEffect(() => {
    if (generatedQuestions.length === 0) return;
    // allow DOM to update first
    const timer = window.setTimeout(() => {
      loadMathJax(() => {
        console.log('Typesetting generated questions');
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [generatedQuestions]);

  useEffect(() => {
    setParams(prevParams => {
      const currentSections = prevParams.sections;
      const newSections: SectionState[] = [];
      const sectionNames = ['Section A', 'Section B', 'Section C'];

      for (let i = 0; i < numSections; i++) {
        if (currentSections[i]) {
          // Ensure existing sections have all required fields including questionStyle
          newSections.push({ 
            ...currentSections[i], 
            name: sectionNames[i],
            questionStyle: currentSections[i].questionStyle || 'both'
          });
        } else {
          newSections.push({
            id: i + 1,
            name: sectionNames[i],
            questionType: 'Short Answer' as SectionQuestionType,
            numQuestions: 5,
            marks: i === 0 ? 100 : 30,
            isMCQ: false,
            numOptions: 4,
            questionStyle: 'both',
          });
        }
      }
      return { ...prevParams, sections: newSections };
    });
  }, [numSections]);

  const sectionMarksSum = useMemo(() => {
    return params.sections.reduce((acc, sec) => acc + (Number(sec.marks) || 0), 0);
  }, [params.sections]);

  const handleAddTopic = () => {
    const newTopic = currentTopicInput.trim();
    if (newTopic && !params.topics.includes(newTopic)) {
      setParams(prev => ({ ...prev, topics: [...prev.topics, newTopic] }));
      setCurrentTopicInput('');
    }
  };

  const handleTopicInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTopic();
    }
  };

  const handleRemoveTopic = (topicToRemove: string) => {
    setParams(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic !== topicToRemove)
    }));
  };

  const handleSectionChange = useCallback((id: number, field: keyof SectionState, value: string | number | boolean) => {
    setParams(prev => ({
      ...prev,
      sections: prev.sections.map(sec =>
        sec.id === id ? { ...sec, [field]: value } : sec
      )
    }));
  }, []);

  const getFinalParams = () => {
    return {
      ...params,
      examType: params.examType === 'Custom' ? customExamType : params.examType,
    };
  };

  const handleGenerate = async () => {
    // In PDF mode, subject/topics are optional since the document is the source
    if (!pdfMode && (!params.subject || params.topics.length === 0)) {
      alert("Please enter a subject and at least one topic.");
      return;
    }
    
    // Validate PDF mode requirements
    if (pdfMode && !pdfContent) {
      alert("Please upload a PDF document first or disable document-based mode.");
      return;
    }
    
    if (sectionMarksSum !== params.totalMarks) {
      if (!window.confirm("The sum of section marks does not match the total marks for the exam. Do you want to continue anyway?")) {
        return;
      }
    }
    setIsLoading(true);
    setError(null);
    setProgress(0);
    setGeneratedQuestions([]);

    try {
      const finalParams = getFinalParams();
      
      // Use PDF-based generation if in PDF mode
      let questions;
      if (pdfMode && pdfContent) {
        const formattedContent = formatContentForPrompt(pdfContent);
        
        // DEBUG: Log what we're sending to the AI
        console.log('=== PDF QUESTION GENERATION DEBUG ===');
        console.log('PDF Mode:', pdfMode);
        console.log('PDF Content Pages:', pdfContent.pages.length);
        console.log('PDF Title:', pdfContent.metadata.title);
        console.log('Formatted Content Length:', formattedContent.length);
        console.log('First 1000 chars of content:', formattedContent.slice(0, 1000));
        console.log('=====================================');
        
        questions = await generateQuestionsFromPDF({
          ...finalParams,
          documentContent: formattedContent,
          documentTitle: pdfContent.metadata.title
        }, schoolId || undefined);
      } else {
        questions = await generateQuestions(finalParams, undefined, schoolId || undefined);
      }

      // Helper: Strip HTML tags and clean text
      const stripHtml = (html: string | undefined | null): string => {
        if (!html) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      // Helper: Sanitize text for display (remove code blocks, normalize whitespace)
      const sanitize = (s: string | undefined | null): string => {
        if (!s) return '';
        let t = String(s);
        // Remove markdown code ticks
        t = t.replace(/```(?:json)?\s*[\s\S]*?```/g, (m) => m.replace(/```/g, ''));
        t = t.replace(/`([^`]+)`/g, '$1');
        t = t.replace(/`/g, '');
        // Normalize whitespace
        t = t.replace(/[ \t]+/g, ' ');
        t = t.replace(/\s+\n/g, '\n');
        return t.trim();
      };

      // Helper: Clean option text
      const cleanOptions = (opts: any[]): string[] => {
        if (!Array.isArray(opts)) return [];
        return opts.map((op) => sanitize(stripHtml(String(op || ''))));
      };

      // Helper: Determine correct answer index from answer text
      const getCorrectAnswerIndex = (answer: string, options: string[]): number => {
        if (!answer) return 0;
        const cleanAnswer = stripHtml(answer).trim().toUpperCase();
        // Check if answer starts with A, B, C, D, etc.
        const letterMatch = cleanAnswer.match(/^([A-E])/);
        if (letterMatch) {
          const idx = letterMatch[1].charCodeAt(0) - 65;
          if (idx >= 0 && idx < options.length) return idx;
        }
        // Try to match answer content with options
        const lowerAnswer = cleanAnswer.toLowerCase();
        const matchIdx = options.findIndex(opt => lowerAnswer.includes(opt.toLowerCase()));
        if (matchIdx >= 0) return matchIdx;
        return 0;
      };

      // Helper: Format answer with step-by-step line breaks
      const formatModelAnswer = (answer: string, explanation: string): string => {
        let combined = '';
        if (answer) {
          combined += sanitize(stripHtml(answer));
        }
        if (explanation) {
          const cleanExplanation = sanitize(stripHtml(explanation));
          if (cleanExplanation) {
            combined += combined ? '\n\n' : '';
            combined += cleanExplanation;
          }
        }
        // Add line breaks before "Step X:" patterns for readability
        combined = combined.replace(/(Step\s+\d+:)/gi, '\n$1');
        return combined.trim();
      };

      // Interface for parsed sub-question (matching IndependentAssessmentView's SubQuestion)
      interface ParsedSubQuestion {
        id: string;
        type: 'Multiple Choice' | 'Short Answer' | 'Essay' | 'True/False';
        text: string;
        marks: number;
        options?: string[] | null;
        correctAnswerIndex?: number;
        modelAnswer?: string | null;
      }

      // Extract answer AND explanation for a specific sub-part (a, b, c, etc.)
      // Returns formatted text with answer first, then explanation steps
      const extractPartAnswer = (answer: string, explanation: string, partId: string, fullAnswer: string): string => {
        const combinedText = `${answer || ''}\n${explanation || ''}`;
        
        // Find the position of this part label - must be followed by ) or . or : or space, not a digit
        // This distinguishes (a) from (5+3)
        const partLabelRegex = new RegExp(`\\(${partId}\\)(?![0-9])|(?:^|\\s)${partId}\\)(?![0-9])`, 'gim');
        const matches = [...combinedText.matchAll(partLabelRegex)];
        
        if (matches.length === 0) {
          // No match for this part - return empty
          return '';
        }
        
        // Find the start position (after the label)
        const startMatch = matches[0];
        const startPos = startMatch.index! + startMatch[0].length;
        
        // Find the end position - look for the NEXT part label (b), (c), etc.
        // Must be a single letter followed by ) and NOT a digit
        const nextPartLabels = 'bcdefghijklmnopqrstuvwxyz'.split('').filter(l => l > partId);
        let endPos = combinedText.length;
        
        for (const nextLetter of nextPartLabels) {
          const nextLabelRegex = new RegExp(`\\(${nextLetter}\\)(?![0-9])|(?:^|\\s)${nextLetter}\\)(?![0-9])`, 'gim');
          const nextMatch = combinedText.match(nextLabelRegex);
          if (nextMatch) {
            const nextPos = combinedText.indexOf(nextMatch[0]);
            if (nextPos > startPos && nextPos < endPos) {
              endPos = nextPos;
              break; // Found the immediate next part
            }
          }
        }
        
        // Extract the text between start and end
        let extractedText = combinedText.substring(startPos, endPos).trim();
        
        // Clean up: remove leading colons, periods, or spaces
        extractedText = extractedText.replace(/^[:\.\s]+/, '').trim();
        
        if (extractedText.length > 5) {
          return sanitize(stripHtml(extractedText));
        }
        
        // Fallback: return empty
        return '';
      };

      // Parse sub-questions from question text (e.g., "(a) Part 1 (b) Part 2")
      const parseSubQuestions = (
        questionText: string, 
        totalMarks: number,
        answer: string,
        explanation: string
      ): { mainText: string; subQuestions: ParsedSubQuestion[] } => {
        // Regex matches: (a), (b), (c), a), b), c), (i), (ii), i), ii)
        // Must be at start of line or after whitespace, followed by space or punctuation
        const subPartRegex = /(?:^|\n|\s)(\(?(?:[a-z]|[ivx]+)\))(?:[\.\s:])/gi;
        const matches = [...questionText.matchAll(subPartRegex)];
        
        if (matches.length < 2) {
          // Less than 2 sub-parts - treat as regular question
          return { mainText: questionText, subQuestions: [] };
        }
        
        // Extract main question text (before first sub-part)
        const firstMatchIndex = matches[0].index;
        let mainText = '';
        if (firstMatchIndex !== undefined && firstMatchIndex > 5) {
          // There's meaningful text before the first sub-part
          mainText = questionText.substring(0, firstMatchIndex).trim();
        }
        // If main text is too short or empty, keep the full question but note it has parts
        if (!mainText || mainText.length < 10) {
          mainText = questionText.split(/[\(\s][a-z][\)\.]|[\(\s][ivx]+[\)\.]/i)[0].trim();
        }
        if (!mainText || mainText.length < 5) {
          mainText = questionText; // Keep full text if we can't parse properly
        }
        
        // Calculate marks per sub-question (distribute evenly by default)
        const baseMarksPerSub = Math.max(1, Math.floor(totalMarks / matches.length));
        const remainder = totalMarks - (baseMarksPerSub * matches.length);
        
        // Parse each sub-part
        const subQuestions: ParsedSubQuestion[] = [];
        
        for (let i = 0; i < matches.length; i++) {
          const start = matches[i].index!;
          const end = i < matches.length - 1 ? matches[i + 1].index! : questionText.length;
          let subText = questionText.substring(start, end).trim();
          
          // Clean leading "(a)" or "a)" from text
          subText = subText.replace(/^(\(?(?:[a-z]|[ivx]+)\))[\.\s:]*/i, '').trim();
          
          // Extract marks if present, e.g., "(2 marks)" or "[3 marks]"
          let partMarks = baseMarksPerSub + (i < remainder ? 1 : 0); // Distribute remainder
          const markMatch = subText.match(/[\(\[](\d+)\s*marks?[\)\]]/i);
          if (markMatch) {
            partMarks = parseInt(markMatch[1]);
            subText = subText.replace(/[\(\[](\d+)\s*marks?[\)\]]/i, '').trim();
          }
          
          // Extract this part's answer from the full answer/explanation
          const partId = matches[i][1].replace(/[()]/g, '').toLowerCase();
          const fullAnswer = formatModelAnswer(answer, explanation);
          let partAnswer = extractPartAnswer(answer, explanation, partId, fullAnswer);
          
          // If no specific part answer found, use the full answer for the first part
          // or indicate that answer is part of the main explanation
          if (!partAnswer && i === 0 && fullAnswer) {
            partAnswer = fullAnswer;
          }
          
          subQuestions.push({
            id: Math.random().toString(36).substr(2, 9),
            type: 'Short Answer', // Default for sub-parts
            text: subText,
            marks: partMarks,
            modelAnswer: partAnswer || null
          });
        }
        
        return { mainText, subQuestions };
      };

      // Map AI questions to manual mode format
      const mappedQuestions = questions.map((q: any, idx: number) => {
        const rawQuestionText = sanitize(stripHtml(q.question || '')).replace(/^\d+[\.\)]\s*/, ''); // Remove leading numbering
        const options = cleanOptions(q.options || []);
        const isMCQ = q.type === 'MCQ' || (options.length > 0);
        
        // Determine base question type
        let questionType: 'Multiple Choice' | 'Short Answer' | 'Essay' | 'True/False' = 'Short Answer';
        if (isMCQ) {
          questionType = 'Multiple Choice';
        } else if (q.type === 'Essay') {
          questionType = 'Essay';
        }

        // Map difficulty
        let difficulty: 'Easy' | 'Medium' | 'Hard' = 'Medium';
        if (q.difficulty) {
          const d = String(q.difficulty).toLowerCase();
          if (d === 'easy') difficulty = 'Easy';
          else if (d === 'hard') difficulty = 'Hard';
        }

        // Determine section from original question or default
        const section = q.section || 'Section A';

        // PRIORITY 1: Check for STRUCTURED subQuestions array from AI (scalable approach)
        if (!isMCQ && Array.isArray(q.subQuestions) && q.subQuestions.length > 0) {
          // Helper: Fix orphan labels in answers (e.g., "U1:\n..." becomes "U1 = ...")
          const cleanAnswer = (answer: string): string => {
            if (!answer) return '';
            let cleaned = sanitize(stripHtml(answer));
            // Fix orphan labels: "Label:\n" or "Label:\r\n" -> "Label = "
            cleaned = cleaned.replace(/([A-Za-z0-9]+):\s*\n/g, '$1 = ');
            // Fix labels with just colon and space at start: "U1: " at line start
            cleaned = cleaned.replace(/^([A-Za-z0-9]+):\s+/gm, '$1 = ');
            return cleaned.trim();
          };

          // AI provided structured sub-questions - use directly
          const structuredSubQuestions: ParsedSubQuestion[] = q.subQuestions.map((sq: any) => ({
            id: Math.random().toString(36).substr(2, 9),
            type: 'Short Answer' as const,
            text: sanitize(stripHtml(sq.text || '')),
            marks: Number(sq.marks) || 2,
            modelAnswer: sq.answer ? cleanAnswer(sq.answer) : null
          }));

          const totalMarks = structuredSubQuestions.reduce((sum, sq) => sum + sq.marks, 0);

          // CLEAN main question text - strip any letter prefixes that AI incorrectly included
          // Matches: "a) text", "(a) text", "a. text", "(a). text" at the start
          let cleanMainText = rawQuestionText.replace(/^\s*\(?[a-z]\)?[\.\):]?\s*/i, '').trim();
          
          // If after cleaning we have nothing meaningful, use a default header
          if (cleanMainText.length < 10) {
            cleanMainText = 'Answer the following questions:';
          }

          return {
            id: q.id || Math.random().toString(36).substr(2, 9),
            type: 'Short Answer' as const,
            text: cleanMainText, // Cleaned main statement
            marks: totalMarks || Number(q.marks) || 10,
            difficulty,
            options: null,
            correctAnswerIndex: undefined,
            modelAnswer: null, // Answers are in sub-questions
            learningObjective: q.topic || params.topics[0] || 'AI Generated',
            image: null,
            subQuestions: structuredSubQuestions,
            section,
          };
        }

        // PRIORITY 2: Fallback to parsing sub-questions from text (legacy support)
        if (!isMCQ) {
          const { mainText, subQuestions } = parseSubQuestions(
            rawQuestionText,
            Number(q.marks) || 2,
            q.answer || '',
            q.explanation || ''
          );

          if (subQuestions.length > 0) {
            // Question has sub-parts - structure accordingly
            const totalMarks = subQuestions.reduce((sum, sq) => sum + sq.marks, 0);
            
            return {
              id: q.id || Math.random().toString(36).substr(2, 9),
              type: 'Short Answer' as const, // Container type
              text: mainText,
              marks: totalMarks,
              difficulty,
              options: null,
              correctAnswerIndex: undefined,
              modelAnswer: null, // Answers are in sub-questions
              learningObjective: q.topic || params.topics[0] || 'AI Generated',
              image: null,
              subQuestions,
              section,
            };
          }
        }

        // No sub-questions - return flat question
        const correctAnswerIndex = isMCQ ? getCorrectAnswerIndex(q.answer || '', options) : undefined;
        const modelAnswer = formatModelAnswer(q.answer || '', q.explanation || '');

        return {
          id: q.id || Math.random().toString(36).substr(2, 9),
          type: questionType,
          text: rawQuestionText,
          marks: Number(q.marks) || 2,
          difficulty,
          options: isMCQ ? options : null,
          correctAnswerIndex: isMCQ ? correctAnswerIndex : undefined,
          modelAnswer: modelAnswer || null,
          learningObjective: q.topic || params.topics[0] || 'AI Generated',
          image: null,
          subQuestions: null,
          section,
        };
      });

      // Also store in the old format for display within this component
      const displayQuestions = questions.map((q: any) => {
        const safeQuestion = sanitize(q.question ?? '');
        const safeOptions = cleanOptions(q.options || []);
        let displayAnswer = q.answer || '';
        
        if (q.type === 'MCQ' && safeOptions.length > 0) {
          const idx = getCorrectAnswerIndex(q.answer, safeOptions);
          displayAnswer = `${String.fromCharCode(65 + idx)}. ${safeOptions[idx] || ''}`;
        } else {
          displayAnswer = sanitize(String(q.answer || ''));
        }

        return {
          ...q,
          question: `${safeQuestion.replace(/\n/g, '<br/>')} <span class="font-normal text-gray-500">(${q.marks} marks)</span>`,
          options: safeOptions,
          answer: displayAnswer.replace(/\n/g, '<br/>'),
          explanation: sanitize(String(q.explanation || '')).replace(/\n/g, '<br/>'),
        };
      });

      setGeneratedQuestions(displayQuestions as Question[]);

      // Call the callback if provided (for IndependentAssessmentView integration)
      if (onQuestionsGenerated) {
        onQuestionsGenerated(mappedQuestions as any[], finalParams);
      }
    } catch (err) {
      setError('Failed to generate questions. Please try again.');
      console.error(err);
    } finally {
      setProgress(100);
      setTimeout(() => setIsLoading(false), 500);
    }
  };
  
  const handleSaveExam = () => {
    if (generatedQuestions.length === 0) {
      alert("No questions to save. Please generate an exam first.");
      return;
    }
    setIsSaveModalOpen(true);
    setSaveError(undefined);
  };

  const handleSaveModalSubmit = (examName: string) => {
    if (!examName.trim()) {
      setSaveError("Exam name cannot be empty.");
      return;
    }

    if (savedExams.some(e => e.params.examTitle.toLowerCase() === examName.toLowerCase() && e.id !== currentExamId)) {
      setSaveError("This name already exists. Please choose another name.");
      return;
    }

    const finalParams = getFinalParams();
    finalParams.examTitle = examName.trim();

    const newExam: SavedExam = {
      id: currentExamId || `exam-${Date.now()}`,
      createdAt: currentExamId ? (savedExams.find(e => e.id === currentExamId)?.createdAt || new Date().toISOString()) : new Date().toISOString(),
      params: finalParams as any,
      questions: generatedQuestions
    };
    addSavedExam(newExam);
    setIsSaveModalOpen(false);
    onCancelEdit();
  };

  const handleSaveModalClose = () => {
    setIsSaveModalOpen(false);
    setSaveError(undefined);
  };

  const handleUpdateQuestion = (updatedQuestion: Question) => {
    setGeneratedQuestions(prev => 
      prev.map(q => q.id === updatedQuestion.id ? updatedQuestion : q)
    );
    setEditingQuestionId(null);
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
  };

  const handleGlobalMCQChange = (checked: boolean) => {
    setParams(prev => ({
      ...prev,
      sections: prev.sections.map(sec => ({ ...sec, isMCQ: checked }))
    }));
  };

  // PDF Upload Handlers
  const handlePDFSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validatePDFFile(file);
    if (!validation.valid) {
      setPdfError(validation.error || 'Invalid PDF file');
      return;
    }

    setPdfFile(file);
    setPdfLoading(true);
    setPdfError(null);
    setPdfProgress(0);
    setPdfProgressStatus('Loading PDF...');

    try {
      const result = await extractTextFromPDF(file, (progress, status) => {
        setPdfProgress(progress);
        setPdfProgressStatus(status);
      });
      setPdfContent(result);
      setPdfMode(true);
      
      // Auto-populate fields from PDF
      const pdfTitle = result.metadata.title || file.name.replace('.pdf', '');
      
      // Set the PDF title as the topic and exam title
      setParams(prev => ({
        ...prev,
        examTitle: `${pdfTitle} - Similar Questions`,
        topics: [pdfTitle],
      }));
      
      console.log(`Extracted ${result.pages.length} pages from PDF: "${pdfTitle}"${result.usedOCR ? ' (used OCR)' : ''}`);
    } catch (err) {
      console.error('PDF extraction failed:', err);
      setPdfError('Failed to extract text from PDF. The file may be corrupted or password-protected.');
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
      setPdfProgress(100);
      setPdfProgressStatus('');
    }
  };

  const handlePDFRemove = () => {
    setPdfFile(null);
    setPdfContent(null);
    setPdfMode(false);
    setPdfError(null);
    if (pdfInputRef.current) {
      pdfInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6 font-roboto">
      <SaveExamModal
        isOpen={isSaveModalOpen}
        onClose={handleSaveModalClose}
        onSave={handleSaveModalSubmit}
        initialName={params.examTitle}
        errorMessage={saveError}
      />
      <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
        <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg sm:text-xl text-black dark:text-white">{isEditing ? `Editing Exam: ${params.examTitle}` : 'Generate Exam Questions'}</CardTitle>
            {isEditing && (
              <Button 
                variant="outline"
                onClick={onCancelEdit}
                className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
              >
                Cancel Edit & Back to Saved
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-6">
          {!isEditing && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-[#E2E8F0] dark:border-[#1E293B] pb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Exam Title</label>
                  <Input 
                    placeholder="e.g., Midterm Exam" 
                    value={params.examTitle} 
                    onChange={e => setParams({...params, examTitle: e.target.value})}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Exam Type</label>
                  <Select 
                    value={params.examType} 
                    onChange={e => setParams({...params, examType: e.target.value})}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  >
                    {EXAM_TYPE_OPTIONS.map(o => (
                      <option key={o} value={o} className="bg-white dark:bg-[#020817] text-black dark:text-white">
                        {o}
                      </option>
                    ))}
                  </Select>
                  {params.examType === 'Custom' && (
                    <Input 
                      className="mt-2 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                      placeholder="Enter custom exam type"
                      value={customExamType}
                      onChange={e => setCustomExamType(e.target.value)}
                    />
                  )}
                </div>
              </div>
              
              {/* PDF Upload Section */}
              <div className="border-b border-[#E2E8F0] dark:border-[#1E293B] pb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Document-Based Generation
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Upload a PDF to generate questions strictly from its content (prevents AI hallucination)
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={pdfMode}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          handlePDFRemove();
                        } else if (!pdfFile) {
                          pdfInputRef.current?.click();
                        }
                        setPdfMode(e.target.checked);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                {/* Hidden file input */}
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePDFSelect}
                  className="hidden"
                />
                
                {/* PDF Upload Area */}
                {pdfMode && (
                  <div className="mt-4">
                    {!pdfFile ? (
                      <button
                        type="button"
                        onClick={() => pdfInputRef.current?.click()}
                        disabled={pdfLoading}
                        className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                      >
                        {pdfLoading ? (
                          <div className="space-y-4">
                            <div className="w-12 h-12 mx-auto border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              {pdfProgressStatus || 'Processing...'}
                            </p>
                            <div className="w-full max-w-xs mx-auto bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${pdfProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {pdfProgress}% complete
                            </p>
                          </div>
                        ) : (
                          <>
                            <FileText className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              Click to upload an exam PDF (max 15MB)
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              AI will analyze existing questions and generate similar new ones
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              Supports text PDFs and scanned documents (OCR)
                            </p>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                                {pdfFile.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {pdfContent ? `${pdfContent.metadata.pageCount} pages • ${(pdfFile.size / 1024).toFixed(1)} KB` : 'Processing...'}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handlePDFRemove}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        
                        {/* Content Preview */}
                        {pdfContent && (
                          <details className="mt-4">
                            <summary className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                              Preview extracted content ({pdfContent.text.length.toLocaleString()} characters)
                            </summary>
                            <div className="mt-2 max-h-40 overflow-y-auto text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 rounded p-3 font-mono whitespace-pre-wrap">
                              {pdfContent.text.slice(0, 2000)}...
                            </div>
                          </details>
                        )}
                        
                        <div className="mt-3 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                          <Sparkles className="w-4 h-4" />
                          Questions will be generated only from this document
                        </div>
                      </div>
                    )}
                    
                    {pdfError && (
                      <p className="mt-2 text-sm text-red-500">{pdfError}</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-[#E2E8F0] dark:border-[#1E293B] pb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Subject</label>
                  <Input 
                    placeholder="Subject (e.g., Mathematics)" 
                    value={params.subject} 
                    onChange={e => setParams({...params, subject: e.target.value})}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Topic(s)</label>
                  <div className="flex items-center gap-2">
                    <Input 
                      placeholder="Type a topic and press Enter or click Add" 
                      value={currentTopicInput}
                      onChange={e => setCurrentTopicInput(e.target.value)}
                      onKeyDown={handleTopicInputKeyDown}
                      className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                    />
                    <Button 
                      type="button" 
                      onClick={handleAddTopic} 
                      variant="outline"
                      className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2 min-h-[24px]">
                    {params.topics.map(topic => (
                      <span 
                        key={topic} 
                        className="flex items-center gap-1.5 bg-[#DBEAFE] dark:bg-[#1E3A8A] text-[#2563EB] dark:text-[#3B82F6] rounded-full px-3 py-1 text-sm"
                      >
                        {topic}
                        <button 
                          onClick={() => handleRemoveTopic(topic)} 
                          className="text-[#2563EB] dark:text-[#3B82F6] hover:text-[#1E40AF] dark:hover:text-[#1E40AF]" 
                          aria-label={`Remove ${topic}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Difficulty</label>
                  <Select 
                    value={params.difficulty} 
                    onChange={e => setParams({...params, difficulty: e.target.value as QuestionDifficulty})}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  >
                    {DIFFICULTY_OPTIONS.map(o => (
                      <option key={o} value={o} className="bg-white dark:bg-[#020817] text-black dark:text-white">
                        {o}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Number of Sections</label>
                  <Select 
                    value={numSections.toString()} 
                    onChange={e => setNumSections(parseInt(e.target.value))}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  >
                    <option value={1} className="bg-white dark:bg-[#020817] text-black dark:text-white">1 Section</option>
                    <option value={2} className="bg-white dark:bg-[#020817] text-black dark:text-white">2 Sections</option>
                    <option value={3} className="bg-white dark:bg-[#020817] text-black dark:text-white">3 Sections</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Marks for Exam</label>
                  <Input 
                    type="number" 
                    value={params.totalMarks} 
                    onChange={e => setParams({...params, totalMarks: parseInt(e.target.value) || 0})}
                    className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  />
                </div>
              </div>
              
              <div className="mt-6 space-y-6">
                {params.sections.map((sec, _index) => (
                  <div key={sec.id} className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg p-4 bg-white dark:bg-[#020817]">
                    <h4 className="font-semibold text-lg mb-4 text-black dark:text-white">{sec.name}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Question Type</label>
                        <Select 
                          value={sec.questionType} 
                          onChange={e => handleSectionChange(sec.id, 'questionType', e.target.value as SectionQuestionType)}
                          disabled={sec.isMCQ}
                          className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                        >
                          <option value="Short Answer" className="bg-white dark:bg-[#020817] text-black dark:text-white">Short Answer</option>
                          <option value="Essay" className="bg-white dark:bg-[#020817] text-black dark:text-white">Essay</option>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Question Style</label>
                        <Select 
                          value={sec.questionStyle || 'both'} 
                          onChange={e => handleSectionChange(sec.id, 'questionStyle', e.target.value)}
                          className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                        >
                          <option value="single" className="bg-white dark:bg-[#020817] text-black dark:text-white">Single Questions</option>
                          <option value="sub-questions" className="bg-white dark:bg-[#020817] text-black dark:text-white">Sub-Questions</option>
                          <option value="both" className="bg-white dark:bg-[#020817] text-black dark:text-white">Both (Mixed)</option>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Number of Questions</label>
                        <Input 
                          type="number" 
                          value={sec.numQuestions} 
                          onChange={e => handleSectionChange(sec.id, 'numQuestions', parseInt(e.target.value) || 0)}
                          className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Section Marks</label>
                        <Input 
                          type="number" 
                          value={sec.marks} 
                          onChange={e => handleSectionChange(sec.id, 'marks', parseInt(e.target.value) || 0)}
                          className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                        />
                      </div>
                    </div>
                    {sec.isMCQ && (
                      <div className="pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Number of Options</label>
                        <Input
                          type="number"
                          placeholder="e.g., 4 for A,B,C,D"
                          value={sec.numOptions || 4}
                          onChange={e => handleSectionChange(sec.id, 'numOptions', parseInt(e.target.value) || 4)}
                          min="2"
                          max="5"
                          className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div className="pt-4 border-t border-[#E2E8F0] dark:border-[#1E293B] grid grid-cols-1 md:grid-cols-2 gap-4 items-center mb-2">
                  <Checkbox
                    id="isMCQ-global"
                    label="Enable Options (Multiple-Choice Format)"
                    checked={params.sections.every(sec => !!(sec as any).isMCQ)}
                    onChange={e => handleGlobalMCQChange(e.target.checked)}
                    className="text-gray-600 dark:text-gray-400"
                  />
                </div>
                <div className={`text-right font-medium text-sm p-2 rounded-md border ${sectionMarksSum !== params.totalMarks ? 'text-destructive bg-destructive/10 border-destructive/20' : 'text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-[#E2E8F0] dark:border-[#1E293B]'}`}>
                  Sum of Section Marks: {sectionMarksSum} / {params.totalMarks}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Custom Instructions (Optional)</label>
                <Textarea 
                  placeholder="e.g., Focus on real-life biology examples in photosynthesis. Emphasize factorization problems."
                  value={params.customInstructions}
                  onChange={e => setParams({...params, customInstructions: e.target.value})}
                  className="min-h-[100px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  rows={4}
                />
              </div>

              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={handleGenerate} 
                  disabled={isLoading}
                  className="m-1 h-10 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg"
                >
                  {isLoading ? 'Generating...' : 'Generate Questions'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {isLoading && (
        <div className="m-2 flex flex-col items-center justify-center text-center p-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
          <p className="text-lg font-semibold text-black dark:text-white mb-2">Generating your exam...</p>
          <div className="w-full max-w-md">
            <Progress 
              value={progress} 
              className="h-2 bg-gray-200 dark:bg-gray-700 [&>div]:bg-[#2563EB] dark:[&>div]:bg-[#3B82F6] rounded-lg"
            />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-2">{Math.round(progress)}% Complete</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">The AI is crafting the questions and marking scheme.</p>
        </div>
      )}
      {error && (
        <div className="m-2 text-center p-8 text-destructive bg-destructive/10 border border-destructive/20 rounded-lg shadow-sm">
          <p className="font-semibold text-destructive">An Error Occurred</p>
          <p className="text-sm mt-1 text-destructive">{error}</p>
        </div>
      )}

      {generatedQuestions.length > 0 && (
        <div className="space-y-6">
          <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
            <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg sm:text-xl text-black dark:text-white">Export & Save Options</CardTitle>
                <div className="flex items-center space-x-2">
                  <Button 
                    onClick={() => addQuestionsToBank(generatedQuestions)} 
                    variant="outline"
                    className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                  >
                    Add to Question Bank
                  </Button>
                  <Button 
                    onClick={handleSaveExam}
                    className="m-1 h-10 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg"
                  >
                    {isEditing ? 'Update Exam' : 'Save Exam'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 sm:p-6">
              <Checkbox 
                id="include-answers"
                label="Include Answers / Marking Scheme"
                checked={includeAnswers}
                onChange={e => setIncludeAnswers(e.target.checked)}
                className="text-gray-600 dark:text-gray-400"
              />
              <div className="flex items-center space-x-2">
                <Button 
                  onClick={() => exportPDF(getFinalParams(), generatedQuestions, includeAnswers, headerSettings)} 
                  variant="outline"
                  className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                >
                  Export as PDF
                </Button>
                <Button 
                  onClick={() => exportWord(getFinalParams(), generatedQuestions, includeAnswers, headerSettings)} 
                  variant="outline"
                  className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                >
                  Export as Word
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-black dark:text-white">Generated Questions Preview</h3>
            {generatedQuestions.map((q) => {
              const displayAnswer = q.answer;
              const options = Array.isArray((q as any).options) ? (q as any).options as string[] : [];

              return editingQuestionId === q.id ? (
                <EditableQuestionCard 
                  key={q.id}
                  question={q}
                  onSave={handleUpdateQuestion}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <Card 
                  key={q.id}
                  className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm"
                >
                  <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
                    <div className="flex justify-between items-start">
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Section: {q.section}</span>
                        <span>Subject: {q.subject}</span>
                        <span>Topic: {q.topic}</span>
                        <span>Type: {q.type}</span>
                        <span>Difficulty: {q.difficulty}</span>
                        <span>Marks: {q.marks}</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setEditingQuestionId(q.id)}
                        className="m-1 h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                      >
                        Edit
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 sm:p-6">
                    <div className="font-semibold text-black dark:text-white whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: q.question }}></div>
                    
                    {options.length > 0 && q.type === 'MCQ' && (
                      <ol className="list-[upper-alpha] list-inside space-y-2 mt-4 pl-4">
                        {options.map((option, index) => (
                          <li key={index} className="whitespace-pre-wrap">{option}</li>
                        ))}
                      </ol>
                    )}

                    <div>
                      <h4 className="font-bold text-sm text-gray-600 dark:text-gray-400">Answer / Marking Scheme:</h4>
                      <div className="text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-3 rounded-md border border-orange-200 dark:border-orange-900 whitespace-pre-wrap mt-1" dangerouslySetInnerHTML={{ __html: displayAnswer }}></div>
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-gray-600 dark:text-gray-400">Explanation:</h4>
                      <div className="text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-md border border-green-200 dark:border-green-900 whitespace-pre-wrap mt-1" dangerouslySetInnerHTML={{ __html: q.explanation }}></div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
import { onAuthStateChanged } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { LinkIcon } from '../../components/gemini-files/icons/GeneralIcons';
import { Tabs } from "@/components/ui/tabs";
import { FileText, Search, Download, BookOpen, History, Copy, Sparkles, Shield, Star, X } from "lucide-react";
import { generateQuestions, generateQuestionsFromPDF, performResearch, generateCitation, checkPlagiarism, analyzeSourceCredibility, generateSummary, suggestRelatedTopics } from '../../../services/geminiServices';
import { getFirestore, collection, addDoc, updateDoc, doc, getDoc, getDocs, query as fbQuery } from "firebase/firestore";
import { format } from "date-fns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

// Error Boundary Component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="m-2 text-center py-16 text-destructive bg-destructive/10 border border-destructive/20 rounded-lg shadow-sm">
          <p className="font-semibold text-destructive">An Error Occurred</p>
          <p className="text-sm mt-1 text-destructive">Something went wrong. Please try again or contact support.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// MathJax Type Definition
interface MathJax {
  version?: string;
  tex?: any;
  options?: any;
  startup?: {
    promise?: Promise<void>;
    defaultReady?: () => void;
    ready?: () => void;
  };
  typesetPromise?: () => Promise<void>;
}

declare global {
  interface Window {
    MathJax?: MathJax;
  }
}

// Configure MathJax
const configureMathJax = () => {
  if (window.MathJax?.version) {
    console.log('MathJax already configured, version:', window.MathJax.version);
    return;
  }

  window.MathJax = {
    tex: {
      inlineMath: [['$', '$'], ['\\(', '\\)']],
      displayMath: [['$$', '$$'], ['\\[', '\\]']],
      processEscapes: true,
      processEnvironments: true,
    },
    options: {
      skipHtmlTags: ['noscript', 'style', 'textarea', 'pre', 'code'],
      ignoreHtmlClass: 'tex2jax_ignore',
    },
    startup: {
      ready: () => {
        console.log('MathJax is loaded and ready');
        window.MathJax?.startup?.defaultReady?.();
      },
    },
  };
};

// Load MathJax with retry mechanism
const loadMathJax = (callback?: () => void) => {
  if (document.getElementById('mathjax-script')) {
    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise.then(() => {
        window.MathJax?.typesetPromise?.().then(() => {
          console.log('MathJax typeset completed');
          callback?.();
        }).catch(err => console.error('MathJax typeset error:', err));
      }).catch(err => console.error('MathJax startup promise error:', err));
    } else {
      let attempts = 0;
      const maxAttempts = 20;
      const interval = setInterval(() => {
        if (window.MathJax?.startup?.promise) {
          clearInterval(interval);
          window.MathJax.startup.promise.then(() => {
            window.MathJax?.typesetPromise?.().then(() => {
              console.log('MathJax typeset completed after polling');
              callback?.();
            }).catch(err => console.error('MathJax typeset error:', err));
          }).catch(err => console.error('MathJax startup promise error:', err));
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          console.error('MathJax failed to load after max attempts');
        }
        attempts++;
      }, 500);
    }
    return;
  }

  configureMathJax();
  const script = document.createElement('script');
  script.id = 'mathjax-script';
  script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3.2.2/es5/tex-mml-chtml.js';
  script.async = true;
  script.onload = () => {
    console.log('MathJax script loaded');
    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise.then(() => {
        window.MathJax?.typesetPromise?.().then(() => {
          console.log('MathJax initial typeset completed');
          callback?.();
        }).catch(err => console.error('MathJax initial typeset error:', err));
      }).catch(err => console.error('MathJax startup promise error:', err));
    }
  };
  script.onerror = () => {
    console.error('Failed to load MathJax script');
  };
  document.head.appendChild(script);
};

// Types
interface Affiliation {
  role: string;
  schoolID?: string;
}



interface Page {
  title: string;
  content: React.ReactNode;
}

interface ResearchedQuery {
  subject: string;
  topics: string[];
  areaOfFocus: string;
}

interface ResearchTemplate {
  id: string;
  name: string;
  description: string;
  defaultSubject: string;
  defaultTopics: string[];
  defaultFocus: string;
}

const RESEARCH_TEMPLATES: ResearchTemplate[] = [
  {
    id: 'literature-review',
    name: 'Literature Review',
    description: 'Comprehensive review of existing academic literature',
    defaultSubject: 'Literature',
    defaultTopics: ['Critical Analysis', 'Themes'],
    defaultFocus: 'Focus on scholarly perspectives and critical interpretations'
  },
  {
    id: 'case-study',
    name: 'Case Study',
    description: 'In-depth analysis of specific cases or examples',
    defaultSubject: 'Business',
    defaultTopics: ['Case Analysis', 'Problem Solving'],
    defaultFocus: 'Emphasize practical applications and real-world examples'
  },
  {
    id: 'comparative-analysis',
    name: 'Comparative Analysis',
    description: 'Comparison of theories, methods, or concepts',
    defaultSubject: 'Science',
    defaultTopics: ['Theory Comparison', 'Methods'],
    defaultFocus: 'Highlight similarities, differences, and relative strengths'
  },
  {
    id: 'historical-research',
    name: 'Historical Research',
    description: 'Investigation of historical events and contexts',
    defaultSubject: 'History',
    defaultTopics: ['Historical Events', 'Context'],
    defaultFocus: 'Emphasize chronological development and historical significance'
  }
];

interface ResearchViewProps {
  schoolId?: string | null;
  headerSettings?: HeaderSettings;
}

const ResearchView: React.FC<ResearchViewProps> = ({ schoolId, headerSettings }) => {
  const { toast } = useToast();
  const [subject, setSubject] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [currentTopicInput, setCurrentTopicInput] = useState('');
  const [areaOfFocus, setAreaOfFocus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResearchResult | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [researchedQuery, setResearchedQuery] = useState<ResearchedQuery | null>(null);
  
  // New feature states
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [citationFormat, setCitationFormat] = useState<'APA' | 'MLA' | 'Chicago' | 'Harvard'>('APA');
  const [showCitations, setShowCitations] = useState(false);
  const [citations, setCitations] = useState<Record<number, string>>({});
  const [plagiarismScore, setPlagiarismScore] = useState<number | null>(null);
  const [credibilityScores, setCredibilityScores] = useState<Record<number, { score: number; reasoning: string }>>({});
  const [showCredibility, setShowCredibility] = useState(false);
  const [relatedTopics, setRelatedTopics] = useState<string[]>([]);
  const [showRelated, setShowRelated] = useState(false);
  const [compressedSummary, setCompressedSummary] = useState<string>('');
  const [annotations, setAnnotations] = useState<Array<{ text: string; note: string; categoryIndex: number }>>([]);
  const [showAnnotations, setShowAnnotations] = useState(false);

  useEffect(() => {
    loadMathJax();
  }, []);

  useEffect(() => {
    let interval: number;
    if (isLoading) {
      setProgress(10);
      interval = window.setInterval(() => {
        setProgress(prev => {
          if (prev < 90) {
            const increment = Math.random() * 10;
            return Math.min(prev + increment, 90);
          } else if (prev < 99) {
            return prev + 0.5;
          } else {
            clearInterval(interval);
            return 99;
          }
        });
      }, 800);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  const handleAddTopic = () => {
    const newTopic = currentTopicInput.trim();
    if (newTopic && !topics.includes(newTopic)) {
      setTopics(prev => [...prev, newTopic]);
      setCurrentTopicInput('');
    }
  };

  const handleTopicInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTopic();
    }
  };

  const handleRemoveTopic = (topicToRemove: string) => {
    setTopics(prev => prev.filter(topic => topic !== topicToRemove));
  };
  
  // Template selection handler
  const handleTemplateSelect = (templateId: string) => {
    const template = RESEARCH_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setSubject(template.defaultSubject);
      setTopics(template.defaultTopics);
      setAreaOfFocus(template.defaultFocus);
    }
  };
  
  // Generate citations for all sources
  const handleGenerateCitations = useCallback(async () => {
    if (!result?.sources) return;
    
    setIsLoading(true);
    const newCitations: Record<number, string> = {};
    
    try {
      await Promise.all(
        result.sources.map(async (source, index) => {
          try {
            const citation = await generateCitation(source, citationFormat);
            newCitations[index] = citation;
          } catch (err) {
            console.error(`Failed to generate citation for source ${index}:`, err);
            newCitations[index] = `${source.web.title}. Retrieved from ${source.web.uri}`;
          }
        })
      );
      
      setCitations(newCitations);
      setShowCitations(true);
      toast({
        title: "Citations Generated",
        description: `Generated ${Object.keys(newCitations).length} citations in ${citationFormat} format.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to generate citations.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [result?.sources, citationFormat, toast]);
  
  // Check plagiarism
  const handleCheckPlagiarism = useCallback(async () => {
    if (!result?.summary && !result?.categories) return;
    
    const contentToCheck = [
      result.summary,
      ...result.categories.map(c => c.content)
    ].join('\n\n');
    
    setIsLoading(true);
    try {
      const plagiarismResult = await checkPlagiarism(contentToCheck);
      setPlagiarismScore(plagiarismResult.score);
      toast({
        title: "Plagiarism Check Complete",
        description: plagiarismResult.details,
        variant: plagiarismResult.score >= 70 ? "default" : "destructive",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to check plagiarism.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [result, toast]);
  
  // Analyze source credibility
  const handleAnalyzeCredibility = useCallback(async () => {
    if (!result?.sources) return;
    
    setIsLoading(true);
    const scores: Record<number, { score: number; reasoning: string }> = {};
    
    try {
      await Promise.all(
        result.sources.map(async (source, index) => {
          try {
            const credibility = await analyzeSourceCredibility(source);
            scores[index] = credibility;
          } catch (err) {
            console.error(`Failed to analyze credibility for source ${index}:`, err);
            scores[index] = { score: 50, reasoning: 'Unable to assess' };
          }
        })
      );
      
      setCredibilityScores(scores);
      setShowCredibility(true);
      toast({
        title: "Credibility Analysis Complete",
        description: `Analyzed ${Object.keys(scores).length} sources.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to analyze credibility.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [result?.sources, toast]);
  
  // Generate compressed summary
  const handleGenerateSummary = useCallback(async () => {
    if (!result?.summary && !result?.categories) return;
    
    const fullContent = [
      result.summary,
      ...result.categories.map(c => `${c.title}\n${c.content}`)
    ].join('\n\n');
    
    setIsLoading(true);
    try {
      const summary = await generateSummary(fullContent, 100);
      setCompressedSummary(summary);
      toast({
        title: "Summary Generated",
        description: `Created a condensed summary.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to generate summary.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [result, toast]);
  
  // Suggest related topics
  const handleSuggestRelatedTopics = useCallback(async () => {
    if (!subject || topics.length === 0) return;
    
    setIsLoading(true);
    try {
      const suggestions = await suggestRelatedTopics(subject, topics);
      setRelatedTopics(suggestions);
      setShowRelated(true);
      toast({
        title: "Related Topics Found",
        description: `Found ${suggestions.length} related research topics.`,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to suggest related topics.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [subject, topics, toast]);
  
  // Add annotation
  const handleAddAnnotation = useCallback((text: string, categoryIndex: number) => {
    const note = prompt('Enter your note for this highlight:');
    if (note) {
      setAnnotations(prev => [...prev, { text, note, categoryIndex }]);
      toast({
        title: "Annotation Added",
        description: "Your note has been saved.",
      });
    }
  }, [toast]);
  
  // Copy to clipboard
  const handleCopyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied",
        description: "Content copied to clipboard.",
      });
    }).catch(() => {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard.",
        variant: "destructive",
      });
    });
  }, [toast]);
  
  const handleResearch = async () => {
    if (!subject.trim() || topics.length === 0) {
      setError("Please enter a subject and at least one topic to research.");
      return;
    }


    setIsLoading(true);
    setError(null);
    setResult(null);
    setProgress(0);
    setResearchedQuery({ subject, topics, areaOfFocus });

    try {
      const researchResult = await performResearch(subject, topics, areaOfFocus, schoolId || undefined);
      setResult(researchResult);
      setCurrentPage(1);
      loadMathJax(() => {
        console.log('Typesetting research result');
      });
    } catch (err: any) {
      console.error(err);
      setError('Failed to conduct research. Please try again.');
      toast({
        title: "Error",
        description: 'Failed to conduct research. Please try again.',
        variant: "destructive",
      });
    } finally {
      setProgress(100);
      setTimeout(() => setIsLoading(false), 500);
    }
  };
  
  const flatPages: Page[] = useMemo(() => {
    if (!result) return [];

    const pages: Page[] = [];

    if (result.summary) {
      pages.push({
        title: "Overall Summary",
        content: (
          <div 
            className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-md mt-3 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] whitespace-pre-wrap mathjax-process" 
            dangerouslySetInnerHTML={{ __html: result.summary }}
          />
        ),
      });
    }

    if (result.categories) {
      result.categories.forEach(category => {
        const paragraphs = category.content.split(/\n{2,}/).filter(p => p.trim() !== '');
        if (paragraphs.length > 0) {
          paragraphs.forEach((paragraph, index) => {
            const pageTitle = paragraphs.length > 1 
              ? `${category.title} (Part ${index + 1})` 
              : category.title;
            pages.push({
              title: pageTitle,
              content: (
                <div 
                  className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-md mt-3 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] whitespace-pre-wrap mathjax-process" 
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              ),
            });
          });
        } else if (category.content.trim()) {
          pages.push({
            title: category.title,
            content: (
              <div 
                className="prose prose-sm dark:prose-invert max-w-none p-4 rounded-md mt-3 bg-white dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] whitespace-pre-wrap mathjax-process" 
                dangerouslySetInnerHTML={{ __html: category.content }}
              />
            ),
          });
        }
      });
    }

    // Always add a Sources page if we have a result, even if sources is an empty array
    // This fulfills the user's expectation of a "page at the end"
    if (result) {
      pages.push({
        title: "Sources & Links",
        content: (
          <div className="space-y-4 mt-3">
            {result.sources && result.sources.length > 0 ? (
              <ul className="space-y-2">
                {result.sources.map((source, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm">
                    <LinkIcon className="w-4 h-4 mt-1 text-gray-400 dark:text-gray-500 shrink-0" />
                    <a 
                      href={source.web.uri} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {source.web.title || source.web.uri}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                <BookOpen className="w-8 h-8 mx-auto text-gray-400 mb-2 opacity-50" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No direct external sources were cited for this specific summary.
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  The AI generated this response based on its internal knowledge base combined with general web grounding.
                </p>
              </div>
            )}
          </div>
        ),
      });
    }

    return pages;
  }, [result]);

  const totalPages = flatPages.length;
  const currentPageData = totalPages > 0 ? flatPages[currentPage - 1] : null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto font-roboto">
      <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
        <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
          <CardTitle className="text-lg sm:text-xl text-black dark:text-white">AI Research Assistant</CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400 pt-1">
            Enter a subject and topic(s) to get a web-grounded, categorized summary from the AI.
          </p>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          {/* Research Templates */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              <Sparkles className="inline w-4 h-4 mr-1" />
              Quick Templates
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {RESEARCH_TEMPLATES.map(template => (
                <Button
                  key={template.id}
                  variant={selectedTemplate === template.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`text-xs ${selectedTemplate === template.id ? 'bg-[#2563EB] dark:bg-[#3B82F6]' : ''}`}
                  disabled={isLoading}
                >
                  {template.name}
                </Button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Subject</label>
              <Input 
                placeholder="e.g., Biology, History" 
                value={subject} 
                onChange={e => setSubject(e.target.value)}
                disabled={isLoading}
                className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Topic(s)</label>
              <div className="flex items-center gap-2">
                <Input 
                  placeholder="Type a topic and press Enter" 
                  value={currentTopicInput}
                  onChange={e => setCurrentTopicInput(e.target.value)}
                  onKeyDown={handleTopicInputKeyDown}
                  disabled={isLoading}
                  className="h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                />
                <Button 
                  type="button" 
                  onClick={handleAddTopic} 
                  variant="outline" 
                  disabled={isLoading}
                  className="m-1 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 min-h-[24px]">
                {topics.map(topic => (
                  <span 
                    key={topic} 
                    className="flex items-center gap-1.5 bg-[#DBEAFE] dark:bg-[#1E3A8A] text-[#2563EB] dark:text-[#3B82F6] rounded-full px-3 py-1 text-sm"
                  >
                    {topic}
                    <button 
                      onClick={() => handleRemoveTopic(topic)} 
                      className="text-[#2563EB] dark:text-[#3B82F6] hover:text-[#1E40AF] dark:hover:text-[#1E40AF]" 
                      aria-label={`Remove ${topic}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Area of Focus (Optional)</label>
            <Textarea
              placeholder="e.g., Emphasize the historical development of calculus and its impact on physics."
              value={areaOfFocus}
              onChange={e => setAreaOfFocus(e.target.value)}
              disabled={isLoading}
              className="min-h-[100px] bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
              rows={3}
            />
          </div>
          <div className="mt-6 flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handleSuggestRelatedTopics}
              disabled={isLoading || !subject || topics.length === 0}
              className="h-10 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Suggest Related Topics
            </Button>
            <Button 
              onClick={handleResearch} 
              disabled={isLoading}
              className="m-1 h-10 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg flex items-center gap-2"
            >
              {isLoading ? (
                'Researching...'
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  Research
                </>
              )}
            </Button>
          </div>
          
          {/* Related Topics Display */}
          {showRelated && relatedTopics.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Related Research Topics
              </h4>
              <div className="flex flex-wrap gap-2">
                {relatedTopics.map((topic, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setTopics([topic]);
                      setShowRelated(false);
                    }}
                    className="text-xs bg-white dark:bg-gray-800 px-3 py-1 rounded-full hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isLoading && (
        <div className="m-2 flex flex-col items-center justify-center text-center p-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
          <p className="text-lg font-semibold text-black dark:text-white mb-2">Conducting Research...</p>
          <div className="w-full max-w-md">
            <Progress 
              value={progress} 
              className="h-2 bg-gray-200 dark:bg-gray-700 [&>div]:bg-[#2563EB] dark:[&>div]:bg-[#3B82F6] rounded-lg"
            />
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-2">{Math.round(progress)}% Complete</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">The AI is searching the web and categorizing the findings.</p>
        </div>
      )}

      {error && (
        <div className="m-2 text-center p-8 text-destructive bg-destructive/10 border border-destructive/20 rounded-lg shadow-sm">
          <p className="font-semibold text-destructive">An Error Occurred</p>
          <p className="text-sm mt-1 text-destructive">{error}</p>
        </div>
      )}

      {result && currentPageData && researchedQuery && (
        <Card className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-sm">
          <CardHeader className="p-4 sm:p-6 border-b border-[#E2E8F0] dark:border-[#1E293B]">
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg sm:text-xl text-black dark:text-white mb-2">
                    Research for "{researchedQuery.topics.join(', ')}" in {researchedQuery.subject}
                  </CardTitle>
                  {plagiarismScore !== null && (
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                      plagiarismScore >= 70 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                      plagiarismScore >= 50 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                      'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      <Shield className="w-4 h-4" />
                      Originality: {plagiarismScore}%
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopyToClipboard(currentPageData.content?.toString() || '')}
                    className="h-8"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCheckPlagiarism}
                    disabled={isLoading}
                    className="h-8"
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    Check
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateSummary}
                    disabled={isLoading}
                    className="h-8"
                  >
                    <FileText className="w-3 h-3 mr-1" />
                    Summarize
                  </Button>
                </div>
              </div>
              
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E2E8F0] dark:border-[#1E293B]">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-600 dark:text-gray-400">Citation Format:</label>
                  <Select
                    value={citationFormat}
                    onChange={e => setCitationFormat(e.target.value as any)}
                    className="h-8 text-xs"
                  >
                    <option value="APA">APA</option>
                    <option value="MLA">MLA</option>
                    <option value="Chicago">Chicago</option>
                    <option value="Harvard">Harvard</option>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateCitations}
                    disabled={isLoading}
                    className="h-8 text-xs"
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    Generate
                  </Button>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAnalyzeCredibility}
                  disabled={isLoading}
                  className="h-8 text-xs"
                >
                  <Star className="w-3 h-3 mr-1" />
                  Analyze Sources
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnnotations(!showAnnotations)}
                  className="h-8 text-xs"
                >
                  <History className="w-3 h-3 mr-1" />
                  Annotations ({annotations.length})
                </Button>
                
                {/* Export Options */}
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const savedResearch: SavedResearch = {
                        id: `research-${Date.now()}`,
                        subject: researchedQuery.subject,
                        topics: researchedQuery.topics,
                        areaOfFocus: researchedQuery.areaOfFocus,
                        result,
                        createdAt: new Date().toISOString(),
                        annotations: annotations.map(a => ({ ...a, id: `ann-${Date.now()}`, timestamp: new Date().toISOString() }))
                      };
                      exportResearchPDF(savedResearch, headerSettings);
                    }}
                    className="h-8 text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const savedResearch: SavedResearch = {
                        id: `research-${Date.now()}`,
                        subject: researchedQuery.subject,
                        topics: researchedQuery.topics,
                        areaOfFocus: researchedQuery.areaOfFocus,
                        result,
                        createdAt: new Date().toISOString(),
                        annotations: annotations.map(a => ({ ...a, id: `ann-${Date.now()}`, timestamp: new Date().toISOString() }))
                      };
                      exportResearchWord(savedResearch, headerSettings);
                    }}
                    className="h-8 text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Word
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportBibliography(result.sources, citationFormat)}
                    className="h-8 text-xs"
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    Bibliography
                  </Button>
                </div>
              </div>
              
              {/* Compressed Summary Display */}
              {compressedSummary && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Compressed Summary</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{compressedSummary}</p>
                </div>
              )}
              
              {/* Annotations Display */}
              {showAnnotations && annotations.length > 0 && (
                <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Your Annotations</h4>
                  <div className="space-y-2">
                    {annotations.map((ann, idx) => (
                      <div key={idx} className="text-sm p-2 bg-white dark:bg-gray-800 rounded">
                        <div className="font-medium text-gray-900 dark:text-gray-100">"{ann.text.substring(0, 50)}..."</div>
                        <div className="text-gray-600 dark:text-gray-400 text-xs mt-1">{ann.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {flatPages.length > 1 && (
                <div className="flex w-full max-w-sm items-center gap-2">
                  <label htmlFor="section-jump" className="text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
                    Jump to section:
                  </label>
                  <Select
                    id="section-jump"
                    value={currentPage.toString()}
                    onChange={e => setCurrentPage(Number(e.target.value))}
                    className="h-10 w-full bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-black dark:text-white rounded-lg focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6]"
                  >
                    {flatPages.map((page, index) => (
                      <option 
                        key={`${index}-${page.title}`} 
                        value={index + 1}
                        className="bg-white dark:bg-[#020817] text-black dark:text-white"
                      >
                        {page.title}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div>
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2 border-b border-[#E2E8F0] dark:border-[#1E293B] pb-2">
                {currentPageData.title}
              </h3>
              <div 
                onMouseUp={() => {
                  const selection = window.getSelection();
                  const text = selection?.toString().trim();
                  if (text && text.length > 10) {
                    const categoryIndex = flatPages.findIndex(p => p.title === currentPageData.title);
                    if (window.confirm('Add this selection as an annotation?')) {
                      handleAddAnnotation(text, categoryIndex);
                    }
                  }
                }}
              >
                {currentPageData.content}
              </div>
            </div>
            
            {/* Citations Display */}
            {showCitations && citations && Object.keys(citations).length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Generated Citations ({citationFormat})
                </h4>
                <div className="space-y-2">
                  {Object.entries(citations).map(([index, citation]) => (
                    <div key={index} className="text-sm text-gray-700 dark:text-gray-300 pl-4">
                      [{parseInt(index) + 1}] {citation}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Source Credibility Display */}
            {showCredibility && result.sources && result.sources.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Source Credibility Analysis
                </h4>
                <div className="space-y-3">
                  {result.sources.map((source, index) => {
                    const credibility = credibilityScores[index];
                    if (!credibility) return null;
                    
                    return (
                      <div key={index} className="p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between mb-2">
                          <a 
                            href={source.web.uri} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex-1"
                          >
                            {source.web.title}
                          </a>
                          <div className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                            credibility.score >= 70 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                            credibility.score >= 50 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                            {credibility.score}%
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{credibility.reasoning}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
          
          {flatPages.length > 1 && (
            <div className="p-6 sm:p-8 border-t border-[#E2E8F0] dark:border-[#1E293B] flex justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="text-sm text-muted-foreground text-center">
                  Showing {currentPage}-{currentPage} of {flatPages.length} results
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`m-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors border border-[#F1F5F9] dark:border-[#1E293B] ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828]'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

interface SavedExamsViewProps {
  savedExams: SavedExam[];
  onViewPDF: (exam: SavedExam) => void;
  onEdit: (exam: SavedExam) => void;
}

const SavedExamsView: React.FC<SavedExamsViewProps> = ({ savedExams, onViewPDF, onEdit }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredExams = useMemo(() => {
    return savedExams
      .filter(exam => exam.params.examTitle.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [savedExams, searchTerm]);

  const totalPages = Math.ceil(filteredExams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExams = filteredExams.slice(startIndex, endIndex);

  return (
    <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
      <div className="flex flex-col space-y-4 h-full min-h-0">
        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full lg:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by exam title..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6] h-10"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {/* Saved Exams Table */}
          <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-black dark:text-white">Saved Exams</CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 min-h-0">
              <div className="h-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                      <TableHead className="min-w-[200px] text-black dark:text-white">Title</TableHead>
                      <TableHead className="min-w-[150px] text-black dark:text-white">Created At</TableHead>
                      <TableHead className="text-right text-black dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedExams.map((exam) => (
                      <TableRow key={exam.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                        <TableCell className="font-medium text-black dark:text-white py-2">
                          {exam.params.examTitle}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-black dark:text-white py-2">
                          {format(new Date(exam.createdAt), "MMM dd, yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          <div className="flex justify-end space-x-2">
                            <Button 
                              variant="outline" 
                              onClick={() => onEdit(exam)}
                              className="m-1 h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                            >
                              Edit
                            </Button>
                            <Button 
                              onClick={() => onViewPDF(exam)}
                              className="m-1 h-8 bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#1E40AF] text-white rounded-lg"
                            >
                              View PDF
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pagination Controls */}
        {filteredExams.length > itemsPerPage && (
          <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B]">
            <div className="text-sm text-muted-foreground text-center">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredExams.length)} of {filteredExams.length} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`m-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors border border-[#F1F5F9] dark:border-[#1E293B] ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828]'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {filteredExams.length === 0 && (
          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
            <CardContent className="text-center py-12">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No exams found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Try adjusting your search
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

interface SavedResearchViewProps {
  savedResearch: SavedResearch[];
  onView: (research: SavedResearch) => void;
  onDelete: (id: string) => void;
  headerSettings?: HeaderSettings;
}

const SavedResearchView: React.FC<SavedResearchViewProps> = ({ savedResearch, onView, onDelete, headerSettings }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredResearch = useMemo(() => {
    return savedResearch
      .filter(research => 
        research.topics.some(t => t.toLowerCase().includes(searchTerm.toLowerCase())) ||
        research.subject.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [savedResearch, searchTerm]);

  const totalPages = Math.ceil(filteredResearch.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResearch = filteredResearch.slice(startIndex, endIndex);

  return (
    <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
      <div className="flex flex-col space-y-4 h-full min-h-0">
        {/* Controls */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full lg:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search by topic or subject..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] h-10"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {/* Research History Table */}
          <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-black dark:text-white flex items-center gap-2">
                <History className="w-5 h-5" />
                Research History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 min-h-0">
              <div className="h-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                      <TableHead className="min-w-[150px] text-black dark:text-white">Subject</TableHead>
                      <TableHead className="min-w-[200px] text-black dark:text-white">Topics</TableHead>
                      <TableHead className="min-w-[150px] text-black dark:text-white">Created</TableHead>
                      <TableHead className="text-right text-black dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedResearch.map((research) => (
                      <TableRow key={research.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                        <TableCell className="font-medium text-black dark:text-white py-2">
                          {research.subject}
                        </TableCell>
                        <TableCell className="text-sm text-black dark:text-white py-2">
                          {research.topics.join(', ')}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-black dark:text-white py-2">
                          {format(new Date(research.createdAt), "MMM dd, yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          <div className="flex justify-end space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => onView(research)}
                              className="h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                            >
                              View
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => exportResearchPDF(research, headerSettings)}
                              className="h-8 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929] rounded-lg"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Delete this research?')) {
                                  onDelete(research.id);
                                }
                              }}
                              className="h-8 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg"
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pagination */}
        {filteredResearch.length > itemsPerPage && (
          <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B]">
            <div className="text-sm text-muted-foreground text-center">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredResearch.length)} of {filteredResearch.length} results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`m-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors border border-[#F1F5F9] dark:border-[#1E293B] ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828]'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="m-1 px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {filteredResearch.length === 0 && (
          <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
            <CardContent className="text-center py-12">
              <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No research history found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm ? 'Try adjusting your search' : 'Your research history will appear here'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

interface ResearchProps {
  layoutType?: 'admin' | 'teacher';
}

const IndependentResearchView: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeSubView, setActiveSubView] = useState<'generator' | 'research' | 'saved' | 'history'>('generator');
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [headerSettings, setHeaderSettings] = useState<HeaderSettings | undefined>(undefined);

  // Fetch header settings when schoolID changes
  useEffect(() => {
    const fetchHeaderSettings = async () => {
      if (!schoolID) return;
      try {
        const headerDoc = await getDoc(doc(db, `teaching_workspaces/${schoolID}/headerSettings/default`));
        if (headerDoc.exists()) {
          setHeaderSettings(headerDoc.data() as HeaderSettings);
        } else {
            // Fallback to initial settings if not found, but we need logo/text from somewhere?
            // For now, undefined is fine, the export utils handle it.
        }
      } catch (error) {
        console.error("Error fetching header settings:", error);
      }
    };
    fetchHeaderSettings();
  }, [schoolID]);

  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [savedExams, setSavedExams] = useState<SavedExam[]>([]);
  const [savedResearch, setSavedResearch] = useState<SavedResearch[]>([]);
  const [selectedExam, setSelectedExam] = useState<SavedExam | null>(null);
  const [examViewerOpen, setExamViewerOpen] = useState(false);
  const [examToView, setExamToView] = useState<SavedExam | null>(null);
  const [viewWithAnswers, setViewWithAnswers] = useState(false);
  const db = getFirestore();

  useEffect(() => {
    loadMathJax();
  }, []);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      // Use user's UID as schoolID to isolate data to this user
      setSchoolID(user.uid);
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
    };
  }, []);

  const loadSavedExams = useCallback(async () => {
    if (!schoolID) return;
    try {
      const q = fbQuery(collection(db, `teaching_workspaces/${schoolID}/savedExams`));
      const snap = await getDocs(q);
      const exams = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as SavedExam));
      setSavedExams(exams.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (e) {
      console.error('Error loading saved exams:', e);
      toast({
        title: "Error",
        description: "Failed to load saved exams.",
        variant: "destructive",
      });
    }
  }, [schoolID, toast]);

  const loadSavedResearch = useCallback(async () => {
    if (!schoolID) return;
    try {
      const q = fbQuery(collection(db, `teaching_workspaces/${schoolID}/savedResearch`));
      const snap = await getDocs(q);
      const research = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      } as SavedResearch));
      setSavedResearch(research.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (e) {
      console.error('Error loading saved research:', e);
      toast({
        title: "Error",
        description: "Failed to load research history.",
        variant: "destructive",
      });
    }
  }, [schoolID, toast]);

  useEffect(() => {
    if (activeSubView === 'saved') {
      loadSavedExams();
    } else if (activeSubView === 'history') {
      loadSavedResearch();
    }
  }, [activeSubView, loadSavedExams, loadSavedResearch]);

  const addQuestionsToBank = useCallback((questions: Question[]) => {
    console.log('Adding questions to bank:', questions);
  }, []);

  const addSavedExam = useCallback(async (exam: SavedExam) => {
    if (!schoolID) return;
    try {
      let docRef;
      if (exam.id && exam.id.startsWith('exam-')) {
        // New exam, remove temporary id
        const { id, ...examData } = exam;
        docRef = await addDoc(collection(db, `teaching_workspaces/${schoolID}/savedExams`), {
          ...examData,
          createdBy: auth.currentUser?.uid,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // Update existing
        docRef = doc(db, `teaching_workspaces/${schoolID}/savedExams/${exam.id}`);
        await updateDoc(docRef, {
          ...exam,
          updatedAt: new Date().toISOString(),
        });
      }
      toast({
        title: "Success",
        description: "Exam saved successfully.",
      });
      loadSavedExams();
    } catch (e) {
      console.error('Error saving exam:', e);
      toast({
        title: "Error",
        description: "Failed to save exam.",
        variant: "destructive",
      });
    }
  }, [schoolID, loadSavedExams, toast]);

  const handleEditExam = (exam: SavedExam) => {
    setSelectedExam(exam);
    setActiveSubView('generator');
  };

  const handleCancelEdit = () => {
    setSelectedExam(null);
    setActiveSubView('saved');
  };

  const handleViewPDF = (exam: SavedExam) => {
    setExamToView(exam);
    setViewWithAnswers(true); // Default to showing answers when viewing
    setExamViewerOpen(true);
  };

  // TODO: Integrate save research functionality into ResearchView component
  // const handleSaveResearch = async (research: SavedResearch) => {
  //   if (!schoolID) return;
  //   try {
  //     await addDoc(collection(db, `schools/${schoolID}/savedResearch`), {
  //       ...research,
  //       createdBy: auth.currentUser?.uid,
  //     });
  //     toast({
  //       title: "Success",
  //       description: "Research saved successfully.",
  //     });
  //     loadSavedResearch();
  //   } catch (e) {
  //     console.error('Error saving research:', e);
  //     toast({
  //       title: "Error",
  //       description: "Failed to save research.",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleViewResearch = (research: SavedResearch) => {
    // For now, just view in the research tab - could be enhanced to load the research
    console.log('Viewing research:', research);
    setActiveSubView('research');
  };

  const handleDeleteResearch = useCallback(async (id: string) => {
    if (!schoolID) return;
    try {
      await updateDoc(doc(db, `teaching_workspaces/${schoolID}/savedResearch/${id}`), {
        deleted: true,
      });
      toast({
        title: "Success",
        description: "Research deleted successfully.",
      });
      loadSavedResearch();
    } catch (e) {
      console.error('Error deleting research:', e);
      toast({
        title: "Error",
        description: "Failed to delete research.",
        variant: "destructive",
      });
    }
  }, [schoolID, loadSavedResearch, toast]);

  if (loading) {
    return (
      <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-white dark:bg-[#020817] flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2563EB] dark:border-[#3B82F6]"></div>
      </div>
    );
  }

  if (error || !schoolID) {
    return (
      <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-auto bg-white dark:bg-[#020817] flex justify-center items-center flex-col gap-4">
        <p className="text-black dark:text-white text-center">{error || "Failed to load."}</p>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="flex flex-col flex-1 h-full overflow-x-hidden">
        <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-y-auto bg-white dark:bg-[#020817] font-roboto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <style dangerouslySetInnerHTML={{__html: `
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
              `}} />
              
              <div className="mb-8 sm:mb-6 w-full max-w-full min-h-0 flex flex-col">
                {examViewerOpen && examToView ? (
                  // Exam Viewer in Content Area
                  <div className="flex flex-col h-full w-full">
                    {/* Viewer Header */}
                    <div className="bg-white dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B] p-4 flex-shrink-0">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-black dark:text-white">
                          {examToView.params.examTitle || `${examToView.params.subject} Exam`}
                        </h2>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setExamViewerOpen(false);
                            setExamToView(null);
                          }}
                          className="h-9"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Close
                        </Button>
                      </div>
                      
                      {/* Controls */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">View Mode: Full Width</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportPDF(examToView.params, examToView.questions, viewWithAnswers, headerSettings)}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Print
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => exportPDF(examToView.params, examToView.questions, viewWithAnswers, headerSettings)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Exam Content */}
                    <ScrollArea className="flex-1 bg-gray-50 dark:bg-gray-900">
                      <div className="flex items-center justify-center min-h-full p-6">
                        <Card className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] shadow-lg max-w-4xl w-full mx-auto">
                          <CardContent className="p-8">
                            {/* Header */}
                            <div className="text-center border-b-2 border-black dark:border-white pb-4 mb-6">
                              <h1 className="text-2xl font-bold mb-2">
                                {examToView.params.examTitle || `${examToView.params.subject} Exam`}
                              </h1>
                              <div className="flex justify-between text-sm mt-2 px-4">
                                <span><strong>Type:</strong> {examToView.params.examType === 'Custom' ? '' : examToView.params.examType}</span>
                                <span><strong>Topic(s):</strong> {examToView.params.topics.join(', ')}</span>
                                <span><strong>Total Marks:</strong> {examToView.params.totalMarks}</span>
                              </div>
                            </div>

                            {/* Sections */}
                            {(() => {
                              const questionsBySection = examToView.questions.reduce((acc, q) => {
                                const sectionName = q.section || 'Uncategorized';
                                if (!acc[sectionName]) {
                                  acc[sectionName] = [];
                                }
                                acc[sectionName].push(q);
                                return acc;
                              }, {} as Record<string, Question[]>);

                              return Object.keys(questionsBySection).sort().map(sectionName => {
                                const sectionConfig = examToView.params.sections.find(s => s.name === sectionName);
                                return (
                                  <div key={sectionName} className="mb-8">
                                    <h2 className="text-xl font-bold border-b border-black dark:border-white pb-2 mb-4">
                                      {sectionName.toUpperCase()} {sectionConfig ? `(${sectionConfig.marks} Marks)` : ''}
                                    </h2>
                                    
                                    {questionsBySection[sectionName].map((q, index) => {
                                      const cleanedQuestion = q.question.replace(/^\d+\.\s*/, '').trim();
                                      return (
                                        <div key={q.id} className="mb-6">
                                          <div className="whitespace-pre-wrap mb-2">
                                            <strong>{index + 1}.</strong> <span dangerouslySetInnerHTML={{ __html: cleanedQuestion }} />
                                          </div>
                                          
                                          {q.type === 'MCQ' && Array.isArray((q as any).options) && (q as any).options.length > 0 && (
                                            <div className="ml-8 mt-2 space-y-1">
                                              {(q as any).options.map((option: string, optIndex: number) => (
                                                <div key={optIndex} className="whitespace-pre-wrap">
                                                  {String.fromCharCode(65 + optIndex)}. {option}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          
                                          {viewWithAnswers && (
                                            <div className="mt-3 ml-4 border-l-2 border-blue-500 pl-3">
                                              <h4 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-1">
                                                Answer/Marking Scheme:
                                              </h4>
                                              <div 
                                                className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-2 rounded whitespace-pre-wrap"
                                                dangerouslySetInnerHTML={{ __html: q.answer }}
                                              />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              });
                            })()}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </div>
                ) : (
                  // Normal Content
                  <>
                <div className="mb-6 flex-shrink-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black dark:text-white mb-2">
                    Research and Generation Tools
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                    AI-powered research and exam question generation
                  </p>
                </div>

                <Tabs 
                  value={activeSubView} 
                  onValueChange={(value: string) => setActiveSubView(value as 'generator' | 'research' | 'saved' | 'history')}
                  className="w-full flex flex-col min-h-0 flex-1 overflow-hidden"
                >
                  <div className="w-full flex justify-center flex-shrink-0 px-2 sm:px-0">
                    <div className="w-full max-w-4xl p-2 bg-white dark:bg-[#020817] border-2 border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-md">
                      <div className="grid grid-cols-4 gap-2">
                        <button
                          onClick={() => setActiveSubView("generator")}
                          className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-2 py-2 sm:py-2.5 rounded-md text-[0.65rem] sm:text-xs font-medium transition-all border border-[#E2E8F0] dark:border-[#1E293B]
                            ${activeSubView === "generator"
                              ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white shadow-md"
                              : "text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929]"
                            }`}
                        >
                          <FileText className="h-4 w-4 sm:h-3 sm:w-3 flex-shrink-0" />
                          <span className="hidden sm:inline">Generate</span>
                          <span className="sm:hidden text-center leading-tight mt-0.5">Generate</span>
                        </button>
                        <button
                          onClick={() => setActiveSubView("research")}
                          className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-2 py-2 sm:py-2.5 rounded-md text-[0.65rem] sm:text-xs font-medium transition-all border border-[#E2E8F0] dark:border-[#1E293B]
                            ${activeSubView === "research"
                              ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white shadow-md"
                              : "text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929]"
                            }`}
                        >
                          <Search className="h-4 w-4 sm:h-3 sm:w-3 flex-shrink-0" />
                          <span className="hidden sm:inline">Research</span>
                          <span className="sm:hidden text-center leading-tight mt-0.5">Research</span>
                        </button>
                        <button
                          onClick={() => setActiveSubView("saved")}
                          className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-2 py-2 sm:py-2.5 rounded-md text-[0.65rem] sm:text-xs font-medium transition-all border border-[#E2E8F0] dark:border-[#1E293B]
                            ${activeSubView === "saved"
                              ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white shadow-md"
                              : "text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929]"
                            }`}
                        >
                          <FileText className="h-4 w-4 sm:h-3 sm:w-3 flex-shrink-0" />
                          <span className="hidden sm:inline">Saved</span>
                          <span className="sm:hidden text-center leading-tight mt-0.5">Saved</span>
                        </button>
                        <button
                          onClick={() => setActiveSubView("history")}
                          className={`flex flex-col sm:flex-row items-center justify-center gap-1 px-2 py-2 sm:py-2.5 rounded-md text-[0.65rem] sm:text-xs font-medium transition-all border border-[#E2E8F0] dark:border-[#1E293B]
                            ${activeSubView === "history"
                              ? "bg-[#2563EB] dark:bg-[#3B82F6] text-white shadow-md"
                              : "text-gray-700 dark:text-gray-300 hover:bg-[#F8FAFC] dark:hover:bg-[#101929]"
                            }`}
                        >
                          <History className="h-4 w-4 sm:h-3 sm:w-3 flex-shrink-0" />
                          <span className="hidden sm:inline">History</span>
                          <span className="sm:hidden text-center leading-tight mt-0.5">History</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 mt-6 w-full overflow-hidden">
                    {activeSubView === "generator" && (
                      <div className="h-full w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <GeneratorView 
                          addQuestionsToBank={addQuestionsToBank} 
                          addSavedExam={addSavedExam} 
                          initialExam={selectedExam} 
                          savedExams={savedExams}
                          onCancelEdit={handleCancelEdit}
                          schoolId={schoolID}
                          headerSettings={headerSettings}
                        />
                      </div>
                    )}
                    {activeSubView === "research" && (
                      <div className="h-full w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <ResearchView schoolId={schoolID} headerSettings={headerSettings} />
                      </div>
                    )}
                    {activeSubView === "saved" && (
                      <div className="h-full w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <SavedExamsView 
                          savedExams={savedExams} 
                          onViewPDF={handleViewPDF} 
                          onEdit={handleEditExam} 
                        />
                      </div>
                    )}
                    {activeSubView === "history" && (
                      <div className="h-full w-full overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <SavedResearchView 
                          savedResearch={savedResearch} 
                          onView={handleViewResearch}
                          onDelete={handleDeleteResearch}
                          headerSettings={headerSettings}
                        />
                      </div>
                    )}
                  </div>
                </Tabs>
                </>
                )}
              </div>
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default IndependentResearchView;